import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { CurrencyMaskConfig } from '@vsolv/vectors-ui/input';
import { Claim } from '@wsphere/warranties/domain';
import { ClaimService } from '../../services';

type ShareControl = 'shop' | 'customer';

interface ApiError {
  status?: number;
  error?: { message?: string | string[] };
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-allocation-dialog',
  templateUrl: './claim-allocation.dialog.html',
  standalone: false,
})
export class ClaimAllocationDialog {
  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private toastSvc: ToastService,
    private claimSvc: ClaimService
  ) {}

  @ViewChild(DialogComponent) private dialog?: DialogComponent;

  @Input() claim?: Claim.Model | null;

  @Output() saved = new EventEmitter<void>();

  readonly currencyMaskConfig: CurrencyMaskConfig = {
    align: 'left',
    allowNegative: false,
    allowZero: true,
    decimal: '.',
    nullable: true,
    precision: 2,
    prefix: '$',
    suffix: '',
    thousands: ',',
    inputMode: 1,
  };

  authorization: Claim.Authorization.Model | null = null;

  saving = false;

  /** The version the split was loaded at; 0 before the first split. */
  private version = 0;

  /** In dollars, as typed. */
  protected form = this.fb.group({
    shop: [null as number | null],
    customer: [null as number | null],
  });

  get customerName() {
    return this.claim?.warranty?.customer?.name ?? null;
  }

  /** Paying the shop needs one assigned to the claim. */
  get shopName() {
    return this.claim?.serviceProviderId ? (this.claim.serviceProviderSnapshot?.name ?? 'Repair shop') : null;
  }

  get currency() {
    return this.authorization?.currency ?? 'USD';
  }

  get netPayable() {
    return this.authorization?.netPayable ?? 0;
  }

  /** In cents. */
  get allocated() {
    const { shop, customer } = this.form.getRawValue();
    return toCents(shop) + toCents(customer);
  }

  get remaining() {
    return this.netPayable - this.allocated;
  }

  /** Same rules as the server: something allocated, all of it, and the shop only when there is one. */
  get isValid() {
    const hasShopShare = toCents(this.form.getRawValue().shop) > 0;
    return this.allocated > 0 && this.remaining === 0 && (!hasShopShare || !!this.shopName);
  }

  /** Opens on the current split; before the first one, all of it goes to the authorization's payee. */
  open(authorization: Claim.Authorization.Model, current: Claim.Allocation.Model[]) {
    this.authorization = authorization;
    this.version = current[0]?.version ?? 0;

    const shareOf = (type: Claim.Authorization.PayeeType) => {
      const amount = current.length
        ? current.find(allocation => allocation.payeeType === type)?.amount
        : authorization.payeeType === type
          ? authorization.netPayable
          : undefined;
      return amount ? amount / 100 : null;
    };

    this.form.reset({
      shop: shareOf(Claim.Authorization.PayeeType.SERVICE_PROVIDER),
      customer: shareOf(Claim.Authorization.PayeeType.CUSTOMER),
    });
    this.dialog?.open();
  }

  close() {
    this.dialog?.close();
  }

  /** Puts whatever isn't allocated yet on this payee. */
  fillRemaining(control: ShareControl) {
    const current = toCents(this.form.controls[control].value);
    this.form.controls[control].setValue(Math.max(current + this.remaining, 0) / 100);
  }

  formatAmount(amount: number) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: this.currency }).format(amount / 100);
  }

  async submit() {
    const claim = this.claim;
    const authorization = this.authorization;
    if (!claim || !authorization || !this.isValid) return;

    const { shop, customer } = this.form.getRawValue();
    const allocations = [
      { payeeType: Claim.Authorization.PayeeType.SERVICE_PROVIDER, amount: toCents(shop) },
      { payeeType: Claim.Authorization.PayeeType.CUSTOMER, amount: toCents(customer) },
    ].filter(allocation => allocation.amount > 0);

    this.saving = true;
    try {
      await this.claimSvc.splitAllocations(claim.id, {
        authorizationId: authorization.id,
        expectedVersion: this.version,
        allocations,
      });

      this.toastSvc.show({
        type: 'success',
        title: 'Payment split saved',
        text: `<strong>Claim #${claim.claimNumber}</strong> pays ${allocations
          .map(allocation => `${this.payeeLabel(allocation.payeeType)} ${this.formatAmount(allocation.amount)}`)
          .join(' and ')}.`,
      });

      this.close();
      this.saved.emit();
    } catch (err) {
      const { status, error } = (err ?? {}) as ApiError;
      const message = error?.message;

      this.toastSvc.show({
        type: 'error',
        title: 'Unable to split the payment',
        text: (Array.isArray(message) ? message.join(' ') : message) || 'Something went wrong. Please try again.',
      });

      // Someone else changed it: reload so the next try starts from the latest split.
      if (status === 409) {
        this.close();
        this.saved.emit();
      }
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

  private payeeLabel(type: Claim.Authorization.PayeeType) {
    return type === Claim.Authorization.PayeeType.SERVICE_PROVIDER
      ? (this.shopName ?? 'the repair shop')
      : (this.customerName ?? 'the customer');
  }
}

function toCents(dollars: number | null | undefined) {
  return dollars ? Math.round(dollars * 100) : 0;
}
